<template>
  <div class="assignment-overview">

      <template v-if="isTeacher">
        <header class="page-header">
            <a href="#" @click.prevent="showGroupSettings(group)" class="tag button-timid left button--show-planning print-hide"><span class="icon icon--inline icon--settings-dark"></span> Planning aanpassen</a>
            <h1>klas {{ group.name }}</h1>
            <hr>
        </header>

        <div class="page-content">
          <term-nav :terms="terms" :currentGroupTermId="currentGroupTerm.id" class="print-hide" />
          <empty-group-message :group="group" v-if="group.students.length === 0 && !group.archived"/>
          <p class="note note--info" >Dit is het overzicht van alle opdrachten voor {{ group.name }}, verdeeld in {{ terms.length }} periodes: <strong>{{ termNames }}</strong>. Elke periode bestaat uit vier blokken met verschillende opdrachten.
            In het linker menu kunt u de periodes en voortgang bekijken. Kies <strong>klas {{ group.name }}</strong> linksboven om direct naar de huidige periode te gaan.</p>
        </div>
      </template>

      <header class="page-header page-header--wide" v-else>
        <h1>Overzicht</h1>
        <p>Hier vind je een overzicht van alle opdrachten voor dit jaar. Wil je vast vooruitwerken? Vraag het eerst even aan je docent. Bekijk <router-link :to="{ name: 'period' }">jouw opdrachtenlijst</router-link> om aan de slag te gaan met de opdrachten van de huidige periode.</p>
        <hr>
      </header>

      <loading v-if="isLoading" :relative="true"></loading>

      <template v-else>
        <div v-for="(term, index) in terms" class="assignment-overview__term">

            <h1 class="assignment-overview__term-title banner"><span>
              <router-link :to="isTeacher ? { name: 'students.overview', params: { groupTermId: term.groupTermId } } : { name: 'period', params: { periodId: term.periods[0].groupPeriodId } }">
                {{ index + 1 }}. {{ term.title }}
              </router-link>
            </span></h1>

            <div v-for="period in term.periods" class="assignment-overview__period">

                <p class="assignment-overview__period-title tag">
                  <router-link :to="{ name: 'period', params: { periodId: period.groupPeriodId } }">{{ period.title }}</router-link><br>
                  <span class="timid">{{ period.start }} t/m {{ period.end }}</span>
                </p>

                <div v-for="assignment in period.assignments" class="assignment-overview__assignment" :class="{ 'assignment-overview__assignment--disabled': isDisabledAssignment(assignment) }">
                    <router-link :to="{ name: 'assignment', params: { assignmentId: assignment.id, periodId: isTeacher ? period.groupPeriodId : undefined /* necessary for teacher, but double redirects if present for student */ } }" class="assignment-overview__link">
                        <img :src="assignment.image" :alt="assignment.title" class="assignment-overview__hero" />
                        <span class="assignment-icon" :class="'icon-' + assignment.type"></span>
                        <h3 class="assignment-overview__title">{{ assignment.title }}</h3>
                        <p class="assignment-overview__subtitle">{{ assignment.subtitle }}</p>
                    </router-link>
                </div>
            </div>

        </div>
      </template>

  </div>
</template>

<script>
import notify from 'js/components/notifications/notificationsMixin'
import track from 'js/helpers/analytics'
import api from 'js/api/resources'
import moment from 'js/helpers/moment'
import { transform } from 'js/vuex/helpers'
import { canTeach } from 'js/api/auth'
import Loading from 'js/components/elements/Loading.vue'
import EmptyGroupMessage from 'js/components/elements/EmptyGroupMessage.vue'
import TermNav from 'js/components/elements/TermNav.vue'
import { getCurrentGroupTermForGroup } from 'js/vuex/getters'

export default {
    components: { EmptyGroupMessage, Loading, TermNav },
    mixins: [ notify ],

    props: {
        group: {},
        isDemo: {
            type: Boolean,
            default: false,
        }
    },

    data: function () {
        return {
            isLoading: true,
        }
    },

    vuex: {
        getters: {
            getCurrentGroupTermForGroup,
        },
        actions: {
            showGroupSettings: ({dispatch}, group) => {
                dispatch('SHOW_MODAL', 'GroupSettings', { group: group, landingPage: 'Planning' })
            },

            getPlanning: ({dispatch}, groupId) => {
                return api.getPlanning( groupId )
                    .then( response => transform(response.body) )
                    .then( result => {
                        dispatch('ADD_GROUP_TERMS', result.groupterms || []);
                        dispatch('ADD_GROUP_PERIODS', result.groupperiods || []);
                        dispatch('ADD_GROUP_ASSIGNMENTS', result.groupassignments || []);

                        dispatch('ADD_TERMS', result.terms || []);
                        dispatch('ADD_PERIODS', result.periods || []);

                        dispatch('ADD_ASSIGNMENTS', result.assignments || []);
                        return result
                    })
            }
        }
    },

    computed: {
        isTeacher: function() {
            return canTeach(this.$store.state.users.current)
        },

        terms: function() {
            // hydrated planning
            // term.periods.assignments
            const store = this.$store.state
            return this.group.groupTerms
              .map(id => store.groups.terms[id])
              .filter(gt => gt.term != null && gt.term.length > 0)
              .map(gt => {
                  let term = store.terms.storage[gt.term[0]]
                  term.groupTermId = gt.id
                  let lastStart = moment( gt.start * 1000 ).format('D MMMM')
                  term.periods = gt.groupPeriods.map ( id => {
                      let gp = store.groups.periods[id]
                      let period = store.periods.storage[gp.period[0]]

                      period.groupPeriodId = id
                      period.start = lastStart
                      period.end = moment( gp.end * 1000 ).format('D MMMM')
                      lastStart = moment( gp.end * 1000 ).add(1, 'days').format('D MMMM')

                      period.assignments = gp.groupAssignments
                          .map( id => store.groups.assignments[id] )
                          .filter( ga => !ga.optional)
                          .map( ga => store.assignments.storage[ga.assignment[0]])

                      return period
                  })
                  return term
              })
              .filter(t => t.periods.length > 0)

        },

        termNames: function() {
            const titles = this.terms.map( t => t.title)

            if ( titles.length < 2 ) {
                return titles[0]
            }

            return titles.slice(0, titles.length-1).join(', ') + ' en ' + titles[ titles.length - 1 ]
        },

        currentGroupTerm: function() {
            return this.getCurrentGroupTermForGroup(this.group.id)
        },
    },

    created () {

        this.fetchPlanning(this.group.id)
            .then( () => this.isLoading = false )

    },

    methods: {

        fetchPlanning: function(groupId) {
            return this.getPlanning(groupId)
                .catch( err => {
                    track.exception('Can\'t load assignments', err)
                    this.$error('Kan opdrachten niet laden')
                })
        },

        isDisabledAssignment: function(assignment) {
            return this.isDemo && !assignment.demo
        }

    }
}
</script>

<style lang="scss">
    @import 'global';
    $assignment-icon-border-size: 3px;
    $assignment-icon-size: 52px;

    .assignment-overview {

        &__term {
          text-align: center;
          margin-bottom: 160px;
        }

        &__term-title {
            text-align: center;
            margin-bottom: -60px;
            a {
              color: $color-typography-inverted;
            }
        }

        &__period {
            margin: 50px 0;
            padding: 0;
            text-align: center;
        }

        &__assignment {
          width: 132px;
          padding: 0;
          margin: 20px 24px;
          display: inline-block;

          text-align: center;
          vertical-align: top;

          &--disabled {
            opacity: 0.3;
            pointer-events: none;
            text-decoration: line-through;
            cursor: not-allowed;
          }
        }

        &__link {
            display: block;
            text-decoration: none;
            position: relative;
            color: $color-typography;
        }

        &__hero {
            width: 132px !important;
            height: 132px;
            border: none;
            border-radius: 50%;
            margin: 1em auto !important;
            display: inline-block !important;
            background-color: $color-lightest-grey;
        }

        &__period-title {
            margin-bottom: 0;
            a {
              color: $color-typography;
            }

            &--error {
                color: $color-warning;
            }
        }

        &__title {
            margin: 0.6em 0 0.2em;
        }

        &__subtitle {
          margin: 0.4em 0;
          font-size: 0.9em;
          line-height: 1.2;
        }

        .assignment-icon {
            position: absolute;
            top: 125px;
            left: 50%;
            margin-left: -($assignment-icon-size+$assignment-icon-border-size)/2;
        }

    }


</style>



// WEBPACK FOOTER //
// TermOverview.vue?7018bc20